import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Home as HomeIcon, SlidersHorizontal, FileText, X, Lock, Minus, Plus, Clock, AlertTriangle } from "lucide-react";
import { colors, type, spacing, radius, TAG_LABELS, ALLERGEN_LABELS, ensureAccent, formatCents } from "@/theme";
import { api, mediaUrl } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";

export default function CustomerMenu() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { session, cartCount, cartTotal, addToCart } = useCustomer();

  const [tenant, setTenant] = useState(null);
  const [cats, setCats] = useState([]);
  const [fascia, setFascia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [allergenFilter, setAllergenFilter] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [allergenOpen, setAllergenOpen] = useState(false);
  const [demoBar, setDemoBar] = useState(true);
  const sectionRefs = useRef({});

  const load = useCallback(async () => {
    try {
      setError(null);
      const [t, m] = await Promise.all([api.getTenant(slug), api.getMenu(slug)]);
      setTenant(t);
      const nonEmpty = (m.categorie || []).filter((c) => c.items.length > 0);
      setCats(nonEmpty);
      setFascia(m.fascia_attiva);
      if (nonEmpty[0]) setActiveCat(nonEmpty[0].id);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tenant?.nome) document.title = `${tenant.nome} — Menu`; }, [tenant?.nome]);

  const sections = useMemo(() => cats.map((c) => ({ title: c.nome, id: c.id, speciale: c.speciale, data: c.items })), [cats]);

  const scrollToCat = (id) => {
    setActiveCat(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const isFlagged = (item) => allergenFilter.size > 0 && item.allergeni.some((a) => allergenFilter.has(a));
  const toggleAllergen = (a) => setAllergenFilter((prev) => { const n = new Set(prev); n.has(a) ? n.delete(a) : n.add(a); return n; });

  const openDish = (item) => {
    if (!item.disponibile || tenant?.aperto_ora === false || tenant?.ordinazione_abilitata === false) return;
    setSelected(item);
  };

  if (loading) return <div style={{ minHeight: "100vh", background: colors.bg, display: "flex", alignItems: "center", justifyContent: "center", ...type.sans, color: colors.accent }}>Caricamento…</div>;
  if (error) return (
    <div style={{ minHeight: "100vh", background: colors.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: spacing.md }}>
      <div style={{ ...type.sans, color: colors.danger }}>{error}</div>
      <button data-testid="retry-button" onClick={load} style={{ background: colors.accent, padding: `${spacing.sm}px ${spacing.lg}px`, border: "none", borderRadius: radius.md, color: colors.onAccent, ...type.sansSemi }}>Riprova</button>
    </div>
  );

  const accent = ensureAccent(tenant?.colore_accento);
  const canOrder = tenant?.ordinazione_abilitata !== false;

  return (
    <div style={{ minHeight: "100vh", background: colors.bg }}>
      <div style={{ maxWidth: 480, margin: "0 auto", position: "relative" }}>
        {/* Header */}
        <div style={{ position: "sticky", top: 0, zIndex: 10, background: colors.bg, borderBottom: `1px solid ${colors.line}`, padding: `${spacing.sm}px ${spacing.lg}px 0` }}>
          {tenant?.is_demo && demoBar ? (
            <div data-testid="demo-bar" style={{ display: "flex", alignItems: "center", gap: spacing.sm, marginBottom: spacing.sm, padding: `2px ${spacing.s12}px`, background: colors.paperDeep, borderRadius: radius.sm }}>
              <span style={{ flex: 1, ...type.sans, fontSize: 13, color: colors.inkMuted }}>
                Questo è un menu di esempio. <span data-testid="demo-bar-cta" onClick={() => navigate("/registrati")} style={{ ...type.sansSemi, color: colors.ink, textDecoration: "underline", cursor: "pointer" }}>Crea il tuo, è gratis</span>
              </span>
              <button data-testid="demo-bar-close" onClick={() => setDemoBar(false)} aria-label="Chiudi" style={{ width: 44, height: 36, background: "none", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={16} color={colors.inkMuted} /></button>
            </div>
          ) : null}
          <div style={{ display: "flex", alignItems: "center", paddingBottom: spacing.sm }}>
            <button data-testid="menu-back-button" onClick={() => navigate("/")} style={{ background: "none", border: "none", display: "flex", padding: 0 }}><HomeIcon size={22} color={colors.ink} /></button>
            <div style={{ flex: 1, marginLeft: spacing.md, minWidth: 0 }}>
              <div style={{ ...type.serif, fontSize: 20, color: colors.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tenant?.nome}</div>
              {session ? <div style={{ ...type.sans, fontSize: 12, color: colors.inkSoft, marginTop: 1 }}>Tavolo {session.etichetta} · {session.guest_name}{fascia ? ` · ${fascia}` : ""}</div> : null}
            </div>
            <button data-testid="allergen-filter-button" onClick={() => setAllergenOpen(true)} style={{ ...iconBtn, background: allergenFilter.size > 0 ? colors.accent : colors.surface, borderColor: allergenFilter.size > 0 ? colors.accent : colors.line }}>
              <SlidersHorizontal size={18} color={allergenFilter.size > 0 ? colors.onAccent : colors.ink} />
            </button>
            {canOrder ? (
              <button data-testid="conto-button" onClick={() => navigate(`/m/${slug}/conto`)} style={iconBtn}><FileText size={18} color={colors.ink} /></button>
            ) : null}
          </div>
          <div className="tv-scroll-x" style={{ display: "flex", gap: spacing.md, overflowX: "auto", paddingBottom: 0 }}>
            {sections.map((s) => (
              <button key={s.id} data-testid={`cat-chip-${s.id}`} onClick={() => scrollToCat(s.id)} style={{ height: 40, padding: `0 ${spacing.xs}px`, borderBottom: `2px solid ${activeCat === s.id ? colors.accent : "transparent"}`, background: "none", border: "none", borderBottomWidth: 2, borderBottomStyle: "solid", borderBottomColor: activeCat === s.id ? colors.accent : "transparent", flexShrink: 0, ...type.sansSemi, fontSize: 12.5, color: activeCat === s.id ? colors.ink : colors.inkFaint, whiteSpace: "nowrap" }}>{s.title}</button>
            ))}
          </div>
        </div>

        {tenant?.aperto_ora === false ? (
          <div data-testid="closed-banner" style={{ display: "flex", alignItems: "center", gap: spacing.sm, background: colors.ink, padding: `${spacing.sm}px ${spacing.lg}px` }}>
            <Clock size={16} color={colors.onAccent} />
            <span style={{ flex: 1, ...type.sansMedium, fontSize: 13, color: colors.onAccent }}>Ordini chiusi in questo momento. {tenant?.prossima_apertura || "Puoi comunque consultare il menu."}</span>
          </div>
        ) : null}

        {allergenFilter.size > 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: spacing.sm, background: colors.paperDeep, padding: `${spacing.sm}px ${spacing.lg}px` }}>
            <AlertTriangle size={14} color={colors.danger} />
            <span style={{ flex: 1, ...type.sans, fontSize: 12, color: colors.danger }}>{tenant?.nota_contaminazione || "Piatti evidenziati contengono l'allergene selezionato."}</span>
          </div>
        ) : null}

        {/* Sections */}
        <div style={{ paddingBottom: (cartCount > 0 ? 100 : 24) }}>
          {sections.map((s) => (
            <div key={s.id} ref={(el) => (sectionRefs.current[s.id] = el)}>
              <div style={{ padding: `${spacing.xl}px ${spacing.lg}px ${spacing.s12}px` }}>
                {s.speciale === "oggi" ? <div style={{ ...type.sansBold, fontSize: 10.5, letterSpacing: 1, color: colors.inkFaint, marginBottom: 2 }}>MENU DEL GIORNO</div> : null}
                <div style={{ ...type.serif, fontSize: 22, color: colors.ink, lineHeight: "25px" }}>{s.title}</div>
              </div>
              {s.data.map((item, idx) => {
                const flagged = isFlagged(item);
                const hasPhoto = !!item.foto_url;
                const off = !item.disponibile;
                return (
                  <div key={item.id}>
                    <div data-testid={`dish-${item.id}`} onClick={() => openDish(item)} style={{ display: "flex", padding: `${spacing.md}px ${spacing.lg}px`, gap: 15, opacity: off ? 0.45 : 1, cursor: off ? "default" : "pointer" }}>
                      {hasPhoto ? (
                        <div style={{ width: 82, height: 102, borderRadius: radius.photo, overflow: "hidden", background: colors.paperDeep, flexShrink: 0, position: "relative" }}>
                          <img src={mediaUrl(item.foto_url)} alt={item.nome} style={{ width: 82, height: 102, objectFit: "cover" }} />
                          <div style={{ position: "absolute", inset: 0, background: "rgba(192,138,90,0.05)" }} />
                        </div>
                      ) : null}
                      <div style={{ flex: 1 }}>
                        <div style={{ ...type.serif, fontSize: hasPhoto ? 19.5 : 22, lineHeight: hasPhoto ? "23px" : "26px", color: flagged ? colors.danger : colors.ink }}>{item.nome}</div>
                        {item.descrizione ? <div style={{ ...type.sans, fontSize: 12.5, color: colors.inkSoft, marginTop: 3, lineHeight: "19px" }}>{item.descrizione}</div> : null}
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: spacing.sm, marginTop: spacing.sm }}>
                          <span style={{ ...type.sansBold, fontSize: 13, color: colors.ink }}>{formatCents(item.prezzo_cent)}</span>
                          {item.tag.map((t) => <span key={t} style={{ border: `1px solid ${colors.rule}`, borderRadius: 2, padding: "2px 6px", ...type.sansBold, fontSize: 10.5, letterSpacing: 1, color: colors.inkFaint }}>{TAG_LABELS[t] || t}</span>)}
                          {off ? <span style={{ border: `1px solid ${colors.danger}`, borderRadius: 2, padding: "2px 6px", background: colors.paperDeep, ...type.sansBold, fontSize: 10.5, letterSpacing: 1, color: colors.danger }}>ESAURITO</span> : null}
                        </div>
                        {flagged ? <div style={{ ...type.sansBold, fontSize: 10.5, letterSpacing: 0.8, color: colors.danger, marginTop: 6 }}>CONTIENE: {item.allergeni.filter((a) => allergenFilter.has(a)).map((a) => (ALLERGEN_LABELS[a] || a).toUpperCase()).join(", ")}</div> : null}
                      </div>
                    </div>
                    {idx < s.data.length - 1 ? <div style={{ height: 1, background: colors.rule, margin: `0 ${spacing.lg}px` }} /> : null}
                  </div>
                );
              })}
            </div>
          ))}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <button data-testid="privacy-link" onClick={() => navigate(`/m/${slug}/privacy`)} style={{ display: "flex", alignItems: "center", gap: spacing.xs, background: "none", border: "none", paddingTop: spacing.lg, paddingBottom: spacing.sm }}>
              <Lock size={13} color={colors.inkFaint} /><span style={{ ...type.sansMedium, fontSize: 13, color: colors.inkFaint, textDecoration: "underline" }}>Informativa privacy</span>
            </button>
            <div style={{ ...type.sans, fontSize: 11, color: colors.inkFaint, paddingBottom: spacing.lg }}>Menu digitale di {tenant?.nome}</div>
          </div>
        </div>

        {/* Cart bar */}
        {cartCount > 0 && tenant?.aperto_ora !== false && canOrder ? (
          <div style={{ position: "fixed", left: 14, right: 14, bottom: spacing.md, maxWidth: 452, margin: "0 auto" }}>
            <button data-testid="cart-bar" onClick={() => navigate(`/m/${slug}/carrello`)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", height: 56, borderRadius: radius.md, border: "none", padding: "0 18px", background: accent, boxShadow: "0 -12px 28px rgba(34,30,24,0.14)" }}>
              <span style={{ color: colors.onAccent, ...type.sansBold, fontSize: 13 }}>{cartCount} {cartCount === 1 ? "piatto" : "piatti"} · vedi ordine</span>
              <span style={{ color: colors.onAccent, ...type.serif, fontSize: 18 }}>{formatCents(cartTotal)}</span>
            </button>
          </div>
        ) : null}
      </div>

      {selected ? <DishModal item={selected} noteRapide={tenant?.note_rapide || []} onClose={() => setSelected(null)} onAdd={(line) => { addToCart(line); setSelected(null); }} /> : null}
      {allergenOpen ? <AllergenModal selected={allergenFilter} onToggle={toggleAllergen} onClear={() => setAllergenFilter(new Set())} onClose={() => setAllergenOpen(false)} /> : null}
    </div>
  );
}

const iconBtn = { width: 40, height: 40, borderRadius: radius.pill, marginLeft: spacing.sm, background: colors.surface, border: `1px solid ${colors.line}`, display: "flex", alignItems: "center", justifyContent: "center" };

function Backdrop({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(26,24,21,0.45)", zIndex: 50, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, maxHeight: "88vh", background: colors.bg, borderTopLeftRadius: radius.sheet, borderTopRightRadius: radius.sheet, overflowY: "auto", boxShadow: "0 -18px 48px rgba(34,30,24,0.28)" }}>
        {children}
      </div>
    </div>
  );
}

function DishModal({ item, noteRapide, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [quick, setQuick] = useState(new Set());
  return (
    <Backdrop onClose={onClose}>
      {item.foto_url ? <img src={mediaUrl(item.foto_url)} alt={item.nome} style={{ width: "100%", height: 220, objectFit: "cover", background: colors.surfaceTertiary }} /> : null}
      <div style={{ padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.sm }}>
        <div style={{ ...type.serif, fontSize: 26, color: colors.ink }}>{item.nome}</div>
        {item.descrizione ? <div style={{ ...type.sans, fontSize: 15, color: colors.inkSoft, lineHeight: "22px" }}>{item.descrizione}</div> : null}
        {item.allergeni.length > 0 ? (
          <div style={{ marginTop: spacing.sm }}>
            <div style={{ ...type.sansSemi, fontSize: 13, color: colors.ink, marginBottom: 6 }}>Allergeni</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.sm }}>
              {item.allergeni.map((a) => <span key={a} style={{ background: colors.dangerSoft, borderRadius: radius.sm, padding: `4px ${spacing.sm}px`, ...type.sansMedium, fontSize: 12, color: colors.danger }}>{ALLERGEN_LABELS[a] || a}</span>)}
            </div>
          </div>
        ) : null}
        {noteRapide.length > 0 ? (
          <div style={{ marginTop: spacing.sm }}>
            <div style={{ ...type.sansSemi, fontSize: 13, color: colors.ink, marginBottom: 6 }}>Note rapide</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.sm }}>
              {noteRapide.map((n) => {
                const on = quick.has(n);
                return <button key={n} data-testid={`quick-note-${n}`} onClick={() => setQuick((p) => { const nx = new Set(p); nx.has(n) ? nx.delete(n) : nx.add(n); return nx; })} style={{ background: on ? colors.ink : colors.surface, border: `1px solid ${on ? colors.ink : colors.line}`, borderRadius: radius.pill, padding: `8px ${spacing.md}px`, ...type.sansMedium, fontSize: 13, color: on ? colors.bg : colors.inkSoft }}>{n}</button>;
              })}
            </div>
          </div>
        ) : null}
        <div style={{ ...type.sansSemi, fontSize: 13, color: colors.ink, marginTop: spacing.sm }}>Nota (max 140)</div>
        <textarea data-testid="dish-note-input" value={note} onChange={(e) => setNote(e.target.value.slice(0, 140))} placeholder="Es. cottura, allergie…" style={{ background: colors.surface, border: `1px solid ${colors.line}`, borderRadius: radius.md, padding: spacing.md, minHeight: 64, ...type.sans, fontSize: 15, color: colors.ink, resize: "vertical" }} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: spacing.md, padding: `${spacing.md}px ${spacing.lg}px`, borderTop: `1px solid ${colors.line}`, background: colors.bg, position: "sticky", bottom: 0 }}>
        <div style={{ display: "flex", alignItems: "center", background: colors.surface, borderRadius: radius.md, border: `1px solid ${colors.line}` }}>
          <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} style={{ width: 44, height: 48, background: "none", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}><Minus size={18} color={colors.ink} /></button>
          <span style={{ ...type.sansSemi, fontSize: 17, color: colors.ink, minWidth: 24, textAlign: "center" }}>{qty}</span>
          <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} style={{ width: 44, height: 48, background: "none", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={18} color={colors.ink} /></button>
        </div>
        <button data-testid="add-to-cart-button" onClick={() => onAdd({ item_id: item.id, nome: item.nome, prezzo_cent: item.prezzo_cent, quantita: qty, nota_riga: note, note_rapide: Array.from(quick) })} style={{ flex: 1, height: 52, background: colors.accent, border: "none", borderRadius: radius.md, color: colors.onAccent, ...type.sansSemi, fontSize: 16 }}>Aggiungi · {formatCents(item.prezzo_cent * qty)}</button>
      </div>
    </Backdrop>
  );
}

function AllergenModal({ selected, onToggle, onClear, onClose }) {
  return (
    <Backdrop onClose={onClose}>
      <div style={{ padding: spacing.lg }}>
        <div style={{ ...type.serif, fontSize: 26, color: colors.ink }}>Evidenzia allergeni</div>
        <div style={{ ...type.sans, fontSize: 15, color: colors.inkSoft, lineHeight: "22px" }}>I piatti che li contengono verranno evidenziati in rosso, non nascosti.</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.sm, marginTop: spacing.md }}>
          {Object.keys(ALLERGEN_LABELS).map((a) => {
            const on = selected.has(a);
            return <button key={a} data-testid={`allergen-${a}`} onClick={() => onToggle(a)} style={{ background: on ? colors.danger : colors.surface, border: `1px solid ${on ? colors.danger : colors.line}`, borderRadius: radius.pill, padding: `8px ${spacing.md}px`, ...type.sansMedium, fontSize: 13, color: on ? "#fff" : colors.inkSoft }}>{ALLERGEN_LABELS[a]}</button>;
          })}
        </div>
        <button data-testid="clear-allergens" onClick={onClear} style={{ marginTop: spacing.lg, width: "100%", height: 48, borderRadius: radius.md, border: `1px solid ${colors.line}`, background: "transparent", ...type.sansSemi, fontSize: 15, color: colors.ink }}>Azzera filtro</button>
      </div>
    </Backdrop>
  );
}
